/**
 * Embeds a bot turn carried that Validate cannot render: a slider, a date
 * picker, a carousel, a custom embed. The text harness keeps only text and
 * buttons, so without this a turn that was just a slider reads as nothing
 * sent, and a run that never showed it can still pass.
 */

import { MAX_EMBEDS, normalizeEmbedType } from '../../utils/embed-types';

/** `app_object` types the page already shows another way, or that carry no embed. */
const NOT_AN_EMBED = new Set(['persistent_menu', 'webview', 'text', 'system']);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

/**
 * The embed types in a turn's frames, in order of first appearance. A frame
 * is an embed when its `app_object` has a `type` outside `NOT_AN_EMBED` and no
 * non-empty `options` (a frame with options is shown as buttons). Types are
 * normalized by `normalizeEmbedType` (the rule the server shares), and one it
 * refuses is dropped; at most `MAX_EMBEDS` are kept.
 */
export const embedsOf = (frames: unknown[]): string[] => {
  const types: string[] = [];
  frames.forEach((frame) => {
    if (!isRecord(frame)) return;
    const app = frame.app_object;
    if (!isRecord(app) || typeof app.type !== 'string') return;
    if (Array.isArray(app.options) && app.options.length > 0) return;
    const type = normalizeEmbedType(app.type);
    if (type === null) return;
    if (NOT_AN_EMBED.has(type) || types.includes(type)) return;
    if (types.length < MAX_EMBEDS) types.push(type);
  });
  return types;
};

/**
 * The types as a reader should see them: `_` and `-` shown as spaces, joined
 * with a comma. Display only; `embedsOf` (the wire value) is not changed.
 */
export const embedTypesList = (types: string[]): string =>
  types.map((type) => type.replace(/[_-]+/g, ' ')).join(', ');
